'use client'

import React, { useEffect, useState } from 'react'
import { Box, Flex, Heading, Image, Text } from '@chakra-ui/react'
import { Article } from '@leid/api-articles'
import { format } from 'date-fns'
import Link from 'next/link'

import { getDateFormatLocale } from 'utils/getDateFormatLocale'
import { handleImageError } from 'utils/imageFallback'
import { normalizeUrl } from 'utils/normalizeUrl'
import { trimStringToLength } from 'utils/trimStringToLength'

type Props = {
  isVertical?: boolean
  article: Article
}

export const FullArticlePreview: React.FC<Props> = ({ isVertical, article }) => {
  const defaultFallback = '/article-big.png'

  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined)
  const [errorLevel, setErrorLevel] = useState(0)

  useEffect(() => {
    if (!article) return

    const primary = article.imageVersions?.cdn?.preview
    const fallback1 = article.image
    const fallback2 = defaultFallback

    let initialSrc = ''
    let initialErrorLevel = 0

    if (primary) {
      initialSrc = primary
      initialErrorLevel = 0
    } else if (fallback1) {
      initialSrc = fallback1
      initialErrorLevel = 1
    } else {
      initialSrc = fallback2
      initialErrorLevel = 2
    }

    setImgSrc((prev) => {
      if (prev === undefined || prev !== initialSrc) {
        setErrorLevel(initialErrorLevel)
        return initialSrc
      }
      return prev
    })
  }, [article])

  const handleImageErrorFallback = () => {
    handleImageError(defaultFallback, imgSrc, errorLevel, setImgSrc, setErrorLevel, article.image)
  }

  if (!article?.body) return null

  return (
    <Link as={`/articles/${normalizeUrl(article.title ?? '', article.id)}`} href="/articles/[title]">
      <Flex direction={{ base: 'column', lg: isVertical ? 'column' : 'row' }}>
        <Image
          borderRadius="9px"
          width={{ base: '100%', lg: isVertical ? '100%' : 'calc(50% - 15px)' }}
          height={{ base: '250px', lg: isVertical ? '250px' : '330px' }}
          src={imgSrc}
          onError={handleImageErrorFallback}
          objectFit="cover"
          alt=""
        />
        <Box
          width={{ base: '100%', lg: isVertical ? '100%' : '50%' }}
          marginLeft={{ base: '0', lg: isVertical ? '0' : '20px' }}
        >
          <Heading
            as="h3"
            fontSize={{ base: 'md', lg: isVertical ? 'md' : 'lg' }}
            color="black"
            fontWeight="normal"
            lineHeight="24px"
            marginTop={{ base: '20px', lg: isVertical ? '20px' : '0' }}
          >
            {article.title}
          </Heading>
          <Box marginTop={{ base: '4px', lg: isVertical ? '4px' : '10px' }}>
            <Text fontSize="xs" color="black" fontWeight={600} lineHeight={1} marginBottom="3px">
              {article.fullName}
            </Text>
            <Text fontSize="xs" color="green.a200" fontWeight={400} lineHeight={1}>
              {format(new Date(article.updatedAt || ''), 'dd.MM.yyyy', {
                locale: getDateFormatLocale(),
              })}
            </Text>
          </Box>
          <Text
            fontSize={{ base: 'sm', lg: isVertical ? 'sm' : 'md' }}
            color="#414C49"
            marginTop={{ base: '8px', lg: isVertical ? '8px' : '12px' }}
            lineHeight="18px"
            fontWeight={300}
          >
            {article?.body ? trimStringToLength(article?.body.replace(/<[^>]*>/g, ' '), 150) : ''}
          </Text>
        </Box>
      </Flex>
    </Link>
  )
}
